import {
  Box,
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Spinner,
  Flex,
  Text,
  HStack,
  useColorModeValue,
} from '@chakra-ui/react';
import React, { useCallback, useEffect, useState } from 'react';
import ReactDatePicker from 'react-datepicker';

import { date as commonDate } from '@finley-technologies/common';
import { Util } from '@finley-technologies/finley-types';

import { useGetPipelineRunsQuery } from '../../requests/tapeBuilder.gql.generated';
import { getMonthDateRange } from './datePickerUtils';
import { datepickerStyles, getHighlightedDates, Legend } from './Legend';
import { SelectedPipeline } from './SelectedPipeline';
import { VersionSelector } from './VersionSelector';
import { colors } from 'packages/common-frontend/colors';

export const CalendarModal = ({
  selectedPipeline,
  setSelectedPipeline,
  onClose,
  pipelineDefinitionId,
}: {
  selectedPipeline: SelectedPipeline | null;
  setSelectedPipeline: (pipeline: SelectedPipeline | null) => void;
  onClose: () => void;
  pipelineDefinitionId: string;
}) => {
  const buttonColorScheme = useColorModeValue('aurora', 'gray');
  const bgColor = useColorModeValue(colors.background[50], 'gray.800');
  const [selectedDate, setSelectedDate] = useState<Util.FullDateStr | null>(
    selectedPipeline?.reportingDate || null,
  );
  const [selectedVersion, setSelectedVersion] = useState<number | null>(
    selectedPipeline?.fileVersion || null,
  );

  useEffect(() => {
    if (selectedPipeline) {
      setSelectedDate(selectedPipeline.reportingDate);
      setSelectedVersion(selectedPipeline.fileVersion);
    }
  }, [selectedPipeline]);

  const [versions, setVersions] = useState<{
    [key: Util.FullDateStr]: number[];
  }>({});
  const [highlightedDates, setHighlightedDates] = useState<Date[]>([]);
  const [currentMonth, setCurrentMonth] = useState<Date>(new Date());

  const { data, loading, error, refetch } = useGetPipelineRunsQuery({
    fetchPolicy: 'network-only',
    variables: {
      pipelineDefinitionId,
      startDate: getMonthDateRange(currentMonth).startDate,
      endDate: getMonthDateRange(currentMonth).endDate,
    },
  });

  useEffect(() => {
    if (data && data.getPipelineRuns) {
      const newVersions: { [key: Util.FullDateStr]: number[] } = {};
      const newHighlightedDates: Date[] = [];
      data.getPipelineRuns.forEach(
        (run: { reportingDate: Util.FullDateStr; fileVersion: number }) => {
          const date = commonDate.fullDateStrToDate(run.reportingDate);
          newHighlightedDates.push(date);
          if (!newVersions[run.reportingDate]) {
            newVersions[run.reportingDate] = [];
          }
          newVersions[run.reportingDate].push(run.fileVersion);
        },
      );
      setVersions(newVersions);
      setHighlightedDates(newHighlightedDates);
    }
  }, [data]);

  const handleConfirm = useCallback(() => {
    if (selectedDate && selectedVersion !== null) {
      let pipelineRunId = null;
      if (data && data.getPipelineRuns) {
        const selectedRun = data.getPipelineRuns.find(
          (run) =>
            run.reportingDate === selectedDate &&
            run.fileVersion === selectedVersion,
        );
        if (selectedRun) {
          pipelineRunId = selectedRun.pipelineRunId;
        }
      }

      setSelectedPipeline({
        pipelineRunId,
        reportingDate: selectedDate,
        fileVersion: selectedVersion,
      });
      onClose();
    }
  }, [selectedDate, selectedVersion, data, onClose, setSelectedPipeline]);

  // decorate highlightedDates with the custom classes
  const customHighlightedDates = getHighlightedDates({
    selectedDate,
    highlightedDates,
  });

  const canSelect = selectedDate !== null && selectedVersion !== null;
  return (
    <Modal isOpen onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent maxWidth="fit-content" bg={bgColor}>
        <ModalHeader>Select Reporting Date</ModalHeader>

        <ModalBody>
          <Legend />
          <Box
            sx={{
              // custom styles for highlighted dates.
              ...datepickerStyles,

              // styles below control the size of the datepicker
              '.react-datepicker': {
                fontSize: '1.2rem',
              },
              '.react-datepicker__day, .react-datepicker__day-name, .react-datepicker__day-number': {
                width: '3rem',
                height: '3rem',
                lineHeight: '3rem',
                margin: '0.2rem',
              },
              '.react-datepicker__header': {
                paddingTop: '1rem',
                paddingBottom: '1rem',
              },
            }}
            width="100%"
          >
            <ReactDatePicker
              selected={
                selectedDate ? commonDate.fullDateStrToDate(selectedDate) : null
              }
              onChange={(date: Date) => {
                const newSelectedDate = commonDate.dateToFullDateString(date);
                setSelectedDate(newSelectedDate);
                // set most recent version for the selected date
                if (
                  versions[newSelectedDate] &&
                  versions[newSelectedDate].length > 0
                ) {
                  setSelectedVersion(versions[newSelectedDate][0]);
                } else {
                  setSelectedVersion(null);
                }
              }}
              highlightDates={customHighlightedDates}
              inline
              monthsShown={1}
              onMonthChange={(date: Date) => {
                setCurrentMonth(date);
                refetch({
                  pipelineDefinitionId,
                  startDate: getMonthDateRange(date).startDate,
                  endDate: getMonthDateRange(date).endDate,
                });
              }}
            />
          </Box>

          {loading ? (
            <Flex justifyContent="center" alignItems="center">
              <Spinner />
              <Text ml={2}>Loading versions...</Text>
            </Flex>
          ) : (
            <VersionSelector
              selectedVersion={selectedVersion}
              versions={selectedDate ? versions[selectedDate] || [] : []}
              setSelectedVersion={setSelectedVersion}
            />
          )}
          {error && <Text color="red.500">Error loading versions</Text>}
        </ModalBody>
        <ModalFooter>
          <HStack spacing={3}>
            <Button
              colorScheme={buttonColorScheme}
              onClick={onClose}
              variant="outline"
            >
              Cancel
            </Button>
            <Button
              isDisabled={!canSelect}
              colorScheme={buttonColorScheme}
              onClick={handleConfirm}
            >
              Select
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
